import * as THREE from 'three';
import { resolveConstellationPlacement } from './constellationPlacement.js';
import { resolveEffectiveEntityShape } from './effectiveEntityShape.js';
import { geometrySpecSpacingDiameter, resolveItemGeometrySpec } from './geometrySpec.js';
import { resolveLabel } from './labelResolution.js';
import { resolveLabelRenderPlan } from './labelRenderPlan.js';
import { resolveConnectorItemRenderPlan } from './connectorRenderPlan.js';
import { toothedBandSamples } from './toothedBand.js';
function anchorContext(frame) {
    return {
        entityConstellations: frame.constellations.filter(c => c.constellationKind === 'entity'),
        entityItemsByCollectionKey: new Map(frame.itemCollections.flatMap(c => c.itemCollectionKind === 'entity' ? [[c.itemCollectionKey, c.items]] : [])),
        itemParametricStateByKey: { ...frame.itemParametricState?.entityItemByKey, ...frame.itemParametricState?.connectorItemByKey },
        entityConstellationParametricStateByKey: frame.constellationParametricState?.entityConstellationByKey ?? {},
    };
}
/** Shape contours use the same local-to-world placement as the rendered items. */
function placedItem(frame, itemKey) {
    const collection = frame.itemCollections.find(c => c.itemCollectionKind === 'entity' && c.items.some(i => i.itemKey === itemKey));
    const constellation = collection && frame.constellations.find(c => c.constellationKind === 'entity'
        && c.itemCollectionKey === collection.itemCollectionKey);
    if (!collection || collection.itemCollectionKind !== 'entity' || !constellation || constellation.constellationKind !== 'entity') {
        throw new Error(`Contour item '${itemKey}' has no entity constellation.`);
    }
    const states = frame.itemParametricState?.entityItemByKey ?? {};
    const constellationStates = frame.constellationParametricState?.entityConstellationByKey ?? {};
    const state = constellationStates[constellation.constellationKey];
    const layout = state?.layout;
    if (!layout)
        throw new Error(`Contour item '${itemKey}' has no layout.`);
    const shapes = new Map(collection.items.map(item => [item.itemKey,
        resolveEffectiveEntityShape({ layout, itemCount: collection.items.length, itemParametricState: states[item.itemKey] })]));
    const spec = resolveItemGeometrySpec({ layout, shape: shapes.get(itemKey),
        itemIndex: collection.items.findIndex(i => i.itemKey === itemKey), itemCount: collection.items.length });
    const placement = resolveConstellationPlacement({
        constellation, constellationState: state, layout, items: collection.items, shapeByItemKey: shapes,
        layoutNormalOffsetByItemKey: new Map(collection.items.map(item => [item.itemKey, states[item.itemKey]?.layoutNormalOffset])),
        spokeSpacingFallback: geometrySpecSpacingDiameter(spec),
        context: anchorContext(frame),
    });
    return { spec, placement, shape: shapes.get(itemKey), item: collection.items.find(item => item.itemKey === itemKey) };
}
export function resolveItemFlag(frame, itemKey) {
    const declaration = frame.itemParametricState?.entityItemByKey?.[itemKey]?.label?.aboveItem;
    if (!declaration?.flag)
        return undefined;
    const { placement, shape, item } = placedItem(frame, itemKey);
    const placed = placement?.itemPlacements.find(item => item.itemKey === itemKey);
    if (!placement || !placed)
        throw new Error(`Flag '${itemKey}' has no placement.`);
    const label = resolveLabel({ item, channel: 'aboveItem', declaration, shape, itemOpacity: 1,
        labelAnchorWorldPosition: placed.labelAnchorWorldPosition,
        ...(placed.labelFrameRotation ? { labelFrameRotation: placed.labelFrameRotation } : {}),
        anchorContext: anchorContext(frame) });
    if (!label?.flag)
        throw new Error(`Flag '${itemKey}' did not resolve.`);
    const plan = resolveLabelRenderPlan(label);
    if (!plan.background)
        throw new Error(`Flag '${itemKey}' has no fly.`);
    const position = new THREE.Vector3(...placed.position)
        .applyEuler(new THREE.Euler(...placement.itemFrameRotation))
        .applyEuler(new THREE.Euler(...placement.frameRotation))
        .add(new THREE.Vector3(...placement.framePosition)).add(new THREE.Vector3(...label.position));
    return { attachment: position.toArray(), postOffset: label.flag.postOffset,
        width: plan.background.width, height: plan.background.height };
}
export function resolveConnectorContourPaths(frame, itemKey) {
    const item = frame.itemCollections.flatMap(collection => collection.itemCollectionKind === 'connector' ? collection.items : [])
        .find(item => item.itemKey === itemKey);
    if (!item)
        throw new Error(`Teaching connector '${itemKey}' is absent.`);
    const plan = resolveConnectorItemRenderPlan(item, { anchors: anchorContext(frame), hoveredEntity: null,
        effectiveActiveItemKeyByConstellationKey: {}, effectiveLinkedActiveItemKeysByConstellationKey: {} });
    if (!plan)
        throw new Error(`Teaching connector '${itemKey}' has unresolved endpoints.`);
    const path = plan.presentation;
    if ('points' in path && path.points)
        return [[...path.points]];
    if (path.kind === 'curve')
        return [new THREE.QuadraticBezierCurve3(new THREE.Vector3(...path.start), new THREE.Vector3(...path.control), new THREE.Vector3(...path.end)).getPoints(32).map(point => point.toArray())];
    if ('start' in path && 'end' in path) {
        const intervals = 'intervals' in path && path.intervals ? path.intervals : [[0, 1]];
        const start = new THREE.Vector3(...path.start), end = new THREE.Vector3(...path.end);
        return intervals.map(([from, to]) => [start.clone().lerp(end, from).toArray(), start.clone().lerp(end, to).toArray()]);
    }
    throw new Error(`Teaching connector '${itemKey}' has an unsupported path.`);
}
export function resolveGuideGeometry(frame, constellationKey) {
    const constellation = frame.constellations.find(c => c.constellationKind === 'entity' && c.constellationKey === constellationKey);
    const collection = constellation && frame.itemCollections.find(c => c.itemCollectionKind === 'entity'
        && c.itemCollectionKey === constellation.itemCollectionKey);
    const items = collection?.itemCollectionKind === 'entity' ? collection.items : [];
    if (!items[0])
        throw new Error(`Guide emphasis '${constellationKey}' has no items.`);
    const { placement } = placedItem(frame, items[0].itemKey);
    if (!placement?.guide)
        throw new Error(`Guide emphasis '${constellationKey}' has no rendered guide.`);
    const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(...placement.frameRotation))
        .multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(...placement.itemFrameRotation)));
    const points = placement.guide.points.map(point => new THREE.Vector3(...point).applyQuaternion(rotation)
        .add(new THREE.Vector3(...placement.framePosition)).toArray());
    if (placement.guide.closed && points[0])
        points.push(points[0]);
    const avoid = items.flatMap(item => {
        const contour = resolveItemContour(frame, item.itemKey, '');
        return contour && 'sphereRadius' in contour ? [{ center: contour.position, radius: contour.sphereRadius * 1.5 }] : [];
    });
    return { key: `guide:${constellationKey}`, points, avoid };
}
export function resolveItemContour(frame, itemKey, color) {
    const { spec, placement } = placedItem(frame, itemKey);
    const item = placement?.itemPlacements.find(i => i.itemKey === itemKey);
    if (!placement || !item)
        return null;
    const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(...placement.frameRotation))
        .multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(...placement.itemFrameRotation)));
    const position = new THREE.Vector3(...item.position).applyQuaternion(rotation).add(new THREE.Vector3(...placement.framePosition));
    rotation.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(...item.rotation)));
    const euler = new THREE.Euler().setFromQuaternion(rotation);
    const base = { key: itemKey, color, position: position.toArray(), rotation: [euler.x, euler.y, euler.z] };
    if (spec.geometryKind === 'sphere')
        return { ...base, sphereRadius: spec.radius + 0.02 };
    if (spec.geometryKind === 'panel' || spec.geometryKind === 'panel-outline') {
        const radius = spec.curvatureRadius;
        const point = (x, y) => radius === undefined ? [x, y, 0.02]
            : [(radius + 0.02) * Math.sin(x / radius), y, (radius + 0.02) * Math.cos(x / radius) - radius];
        const edge = (y, reverse) => Array.from({ length: 33 }, (_, i) => point((reverse ? 0.5 - i / 32 : i / 32 - 0.5) * spec.width, y));
        const points = [...edge(-spec.height / 2, false), ...edge(spec.height / 2, true)];
        return { ...base, points: [...points, points[0]] };
    }
    if (spec.geometryKind === 'torus')
        return { ...base, innerRadius: spec.ringRadius - spec.tubeRadius,
            outerRadius: spec.ringRadius + spec.tubeRadius, innerHalfHeight: 0, outerHalfHeight: 0 };
    if (spec.geometryKind === 'toothed-band') {
        const samples = toothedBandSamples(spec);
        const rim = (corner) => samples.map(sample => {
            const [radius, height] = sample.corners[corner];
            return [Math.sin(sample.angle) * radius, height, Math.cos(sample.angle) * radius];
        });
        return { ...base, points: [...rim(3), ...rim(2).reverse(), rim(3)[0]] };
    }
    if (spec.geometryKind === 'revolved-profile') {
        const inner = Math.min(...spec.profile.map(([radius]) => radius)), outer = Math.max(...spec.profile.map(([radius]) => radius));
        const height = Math.max(...spec.profile.map(([, height]) => Math.abs(height)));
        return { ...base, innerRadius: inner, outerRadius: outer, innerHalfHeight: height, outerHalfHeight: height };
    }
    throw new Error(`Contour item '${itemKey}' has unsupported geometry '${spec.geometryKind}'.`);
}
